/* ===================================================================
   Native Possum — np-predictive.js
   The search dropdown contents, ONE copy for every width (np-header.js
   calls window.npPredictiveSearch.fetch; {mobile: true} on phones).

   PRODUCTS COME FROM THE NP BROWSE ENGINE (/apps/browse/suggest), the same
   index as the results page, so Māori names, misspellings, other countries'
   words and SKUs find things here too, and the dropdown never offers
   nothing for a word the results page answers. The engine sends Liquid-
   rendered rows (snippets/np-ps-product.liquid) — one row markup, in the
   theme, not a second copy in JavaScript.

   PAGES AND JOURNAL POSTS still come from Shopify's own search, which
   indexes them; the engine doesn't.

   "View all results" goes to /apps/browse?q=… (the results page).
   Styles: np-header.css (search panel + dropdown contents).
   Depends on: np-utilities.js (npEscapeHtml).
   =================================================================== */

(function () {
  'use strict';

  var ENGINE = '/apps/browse/suggest';
  var RESULTS = '/apps/browse';
  var CONTENT_SHOWN = 2;

  var productCache = Object.create(null);
  var contentCache = Object.create(null);

  function esc(text) {
    return window.npEscapeHtml ? window.npEscapeHtml(String(text)) : String(text).replace(/[&<>"']/g, '');
  }

  function resultsUrl(query) {
    return RESULTS + '?q=' + encodeURIComponent(query);
  }

  function products(query) {
    if (productCache[query] !== undefined) return Promise.resolve(productCache[query]);
    return fetch(ENGINE + '?q=' + encodeURIComponent(query), { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('suggest ' + r.status);
        return r.text();
      })
      .then(function (html) {
        productCache[query] = html.trim();
        return productCache[query];
      });
  }

  function content(query) {
    if (contentCache[query]) return Promise.resolve(contentCache[query]);
    var root = window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/';
    var url = root + 'search/suggest.json?q=' + encodeURIComponent(query) +
      '&resources[type]=page,article&resources[limit]=4';
    return fetch(url)
      .then(function (r) {
        if (!r.ok) throw new Error('content ' + r.status);
        return r.json();
      })
      .then(function (data) {
        var res = data.resources && data.resources.results ? data.resources.results : {};
        contentCache[query] = (res.articles || []).concat(res.pages || []).slice(0, CONTENT_SHOWN);
        return contentCache[query];
      });
  }

  /* Settled, never rejected: one half failing still shows the other. */
  function settle(promise) {
    return promise.then(
      function (value) { return { ok: true, value: value }; },
      function () { return { ok: false }; }
    );
  }

  function contentHtml(items, mobile) {
    if (!items.length) return '';
    var html = '<div class="predictive-content-divider" aria-hidden="true"></div>';
    if (!mobile) html += '<h2 class="np-ps__heading np-ps__heading--content">Pages &amp; journal</h2>';
    html += '<ul class="predictive-content-links">';
    items.forEach(function (item) {
      var article = item.url && item.url.indexOf('/blogs/') !== -1;
      html += '<li class="predictive-content-item"><a href="' + esc(item.url) + '" class="predictive-content-link">' +
        '<span class="predictive-content-badge">' + (article ? 'Journal' : 'Page') + '</span>' +
        '<span class="predictive-content-title">' + esc(item.title) + '</span></a></li>';
    });
    return html + '</ul>';
  }

  var EMPTY_ICON = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960"><path d="M784-120 532-372q-30 24-69 38t-83 14q-109 0-184.5-75.5T120-580q0-109 75.5-184.5T380-840q109 0 184.5 75.5T640-580q0 44-14 83t-38 69l252 252-56 56ZM380-400q75 0 127.5-52.5T560-580q0-75-52.5-127.5T380-760q-75 0-127.5 52.5T200-580q0 75 52.5 127.5T380-400Z"/></svg>';
  var COLLECTIONS = [
    ['/collections/women', 'Women'], ['/collections/men', 'Men'],
    ['/collections/accessories', 'Accessories'], ['/collections/blankets-throws', 'Blankets + Throws']
  ];

  function emptyHtml(query, mobile) {
    var p = mobile ? 'mobile-predictive-empty' : 'desktop-predictive-empty';
    var title = '<h2 class="' + p + '-title">No results for "' + esc(query) + '"</h2>';
    var icon = '<div class="' + p + '-icon" aria-hidden="true">' + EMPTY_ICON + '</div>';
    return '<div class="' + p + '-state">' + (mobile ? icon + title : title + icon) +
      '<p class="' + p + '-text">Try a different search, or explore our collections below.</p>' +
      '<div class="' + p + '-buttons">' + COLLECTIONS.map(function (c) {
        return '<a href="' + c[0] + '" class="' + p + '-btn">' + c[1] + '</a>';
      }).join('') + '</div>' +
      '<p class="' + p + '-contact">Need help finding something? <a href="/pages/contact">Get in touch</a></p></div>';
  }

  window.npPredictiveSearch = {
    /**
     * Fill `container` with the dropdown for `query`.
     * @param {string} query
     * @param {HTMLElement} container
     * @param {{mobile?: boolean}} [options]
     */
    fetch: function (query, container, options) {
      if (!container) return;
      var mobile = !!(options && options.mobile);
      var loadingClass = mobile ? 'mobile-predictive-loading' : 'predictive-search-loading';
      container.dataset.npQuery = query;
      container.innerHTML = '<div class="' + loadingClass + '">Searching…</div>';
      container.classList.add('active');

      Promise.all([settle(products(query)), settle(content(query))]).then(function (both) {
        /* Typing moved on while this was in the air. */
        if (container.dataset.npQuery !== query) return;
        var rows = both[0].ok ? both[0].value : '';
        var items = both[1].ok ? both[1].value : [];

        if (!rows && !items.length) {
          container.innerHTML = both[0].ok
            ? emptyHtml(query, mobile)
            : '<div class="' + (mobile ? 'mobile-predictive-empty' : 'predictive-search-empty') + '">Unable to load results</div>';
          return;
        }
        container.innerHTML = '<div class="np-ps' + (mobile ? ' np-ps--mobile' : '') + '">' +
          rows + contentHtml(items, mobile) +
          '<a href="' + resultsUrl(query) + '" class="' + (mobile ? 'mobile-predictive-view-all' : 'predictive-search-view-all') + '">View all results</a>' +
          '</div>';
      });
    },

    /** Where a search for `query` lands. */
    resultsUrl: resultsUrl
  };
})();
